import { useCallback, useLayoutEffect, useRef, useState, Suspense, lazy } from "react";
import { useQuery } from "@tanstack/react-query";
import { financeApi, type FinanceApi } from "@bw/shared/api";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useFinanceContext } from "@/hooks/use-finance-context";

const Embed = lazy(() => import("@/components/finance-connect/FinanceConnectEmbed").then(module => ({ default: module.FinanceConnectEmbed })));

export function FinanceAccountSetup({ orgId, api = financeApi, onChanged, showSummary = true }: {
  orgId: string; api?: FinanceApi; onChanged?: () => void; showSummary?: boolean;
}) {
  const { context, actorScope, query, dataKey, pending, readiness, refreshAccount } = useFinanceContext(orgId, api);
  const receiptRead = Boolean(context?.allowed_actions.includes("org:finance_read") && !context.account_bound);
  const intent = useQuery({ queryKey: [...dataKey, "provisioning"], queryFn: ({ signal }) => api.getProvisioning(orgId, signal),
    enabled: receiptRead, retry: false });
  const receipt = intent.isSuccess && intent.data?.org_id === orgId ? intent.data : null;
  const receiptAction = receipt?.allowed_actions.includes("reconcile") ? "reconcile" :
    receipt?.allowed_actions.includes("resume") ? "resume" : null;
  const receiptUnavailable = intent.isError || (intent.isSuccess && intent.data !== null && !receipt);
  const setupAllowed = Boolean(context?.stripe_configuration.state === "configured" && (context.account_bound ?
    context.allowed_session_purposes.includes("onboarding") : context.can_manage_finance_access &&
      intent.isSuccess && !intent.isFetching && (intent.data === null || receiptAction !== null)));
  const [opened, setOpened] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [pollUnavailable, setPollUnavailable] = useState<string | null>(null);
  const scope = context && actorScope ? JSON.stringify([actorScope, context.authority_generation]) : null;
  const identity = actorScope ? JSON.stringify([actorScope.actorUserId, actorScope.orgId, actorScope.workSessionRevision]) : null;
  const currentIdentity = useRef(identity); currentIdentity.current = identity;
  const currentScope = useRef(scope); currentScope.current = scope;
  const mounted = useRef(true);
  const refreshing = useRef(false);
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => { setError(""); setWorking(false); }, [scope]);
  useLayoutEffect(() => { setOpened(null); }, [identity]);
  const refreshStatus = useCallback(async () => {
    if (refreshing.current || !scope || !identity) return null;
    const captured = scope;
    const capturedIdentity = identity;
    refreshing.current = true;
    setError(""); setWorking(true);
    try {
      const result = await refreshAccount();
      if (!mounted.current || currentIdentity.current !== capturedIdentity) return null;
      setPollUnavailable(result.component_support === "verified" ? null : currentScope.current);
      onChanged?.();
      return result;
    } catch {
      if (mounted.current && currentScope.current === captured) {
        setPollUnavailable(captured);
        setError("Payment account status is unavailable. Try refreshing again.");
      }
      return null;
    } finally {
      refreshing.current = false;
      if (mounted.current && currentIdentity.current === capturedIdentity) setWorking(false);
    }
  }, [scope, identity, refreshAccount, onChanged]);
  const refresh = useCallback(async () => { await refreshStatus(); }, [refreshStatus]);
  const poll = Boolean(scope && context?.account_bound && context.allowed_actions.includes("org:finance_read") &&
    context.stripe_configuration.state === "configured" && readiness?.ready !== true &&
    (!readiness || readiness.component_support === "verified") && pollUnavailable !== scope);
  const bootstrap = readiness === null;
  const latestPoll = useRef({ scope, poll, refresh: refreshStatus }); latestPoll.current = { scope, poll, refresh: refreshStatus };
  useLayoutEffect(() => {
    if (!poll || !scope) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      if (cancelled || latestPoll.current.scope !== scope || !latestPoll.current.poll) return;
      const result = await latestPoll.current.refresh();
      if (cancelled || latestPoll.current.scope !== scope || !latestPoll.current.poll ||
          !result || result.ready || result.component_support !== "verified") return;
      timer = setTimeout(() => void tick(), 20_000);
    };
    if (bootstrap) void tick();
    else timer = setTimeout(() => void tick(), 20_000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [api, scope, poll, bootstrap]);
  const start = async () => {
    if (!context || !actorScope || !identity || !setupAllowed) return;
    const capturedIdentity = identity;
    setWorking(true); setError("");
    try {
      if (!context.account_bound) {
        const receipt = await api.ensureProvisioning(orgId);
        if (!mounted.current || currentIdentity.current !== capturedIdentity) return;
        if (!receipt.account_bound) {
          await intent.refetch();
          if (mounted.current && currentIdentity.current === capturedIdentity)
            setError("Account setup is still pending. Reload its status for available actions.");
          return;
        }
      }
      const current = await query.refetch();
      if (!mounted.current || currentIdentity.current !== capturedIdentity) return;
      if (current.isSuccess && current.data?.org_id === orgId &&
          current.data.work_session_revision === actorScope.workSessionRevision && current.data.account_bound &&
          current.data.stripe_configuration.state === "configured" && current.data.allowed_actions.includes("org:finance_read") &&
          current.data.allowed_session_purposes.includes("onboarding"))
        setOpened(JSON.stringify([{ ...actorScope, bindingGeneration: current.data.binding_generation }, current.data.authority_generation]));
      onChanged?.();
    } catch { if (mounted.current && currentIdentity.current === capturedIdentity) {
      await intent.refetch();
      if (mounted.current && currentIdentity.current === capturedIdentity)
        setError("Account setup could not be confirmed. Reload its status before continuing.");
    } }
    finally { if (mounted.current && currentIdentity.current === capturedIdentity) setWorking(false); }
  };
  const fetchSession = useCallback(() => api.createOnboardingSession(orgId), [api, orgId]);
  if (pending) return <CircularProgress aria-label="Loading payment account" size={24} />;
  if (!context || !actorScope) return <Alert severity="error">Payment account access is unavailable.</Alert>;
  const ready = readiness?.ready;
  const onboarding = context.allowed_session_purposes.includes("onboarding");
  return <Stack spacing={2}>
    {showSummary !== false && <Typography variant="body2">{ready === true ? "Payments are ready." : context.account_bound ?
      ready === false ? "Stripe needs additional information." : "Account connected. Refresh to check current readiness." : "No payment account is connected."}</Typography>
    }
    {error && <Alert severity="error">{error}</Alert>}
    {receiptRead && intent.isPending && <CircularProgress aria-label="Loading account setup status" size={20} />}
    {receiptRead && receiptUnavailable && <Alert severity="error">Account setup status is unavailable. Reload before continuing.</Alert>}
    {receipt && !receipt.account_bound && <Alert severity="info">{receipt.status === "needs_reconciliation" ?
      "Existing account setup needs reconciliation." : `Recorded account setup: ${receipt.status.replaceAll("_", " ")}.`}</Alert>}
    {context.stripe_configuration.state === "unavailable" && <Alert severity="info">Stripe controls are unavailable. Retained reports remain readable.</Alert>}
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
      {setupAllowed &&
        <Button size="small" variant="contained" disabled={working} onClick={() => void start()}>
          {context.account_bound ? "Continue in Stripe" : receiptAction === "reconcile" ? "Reconcile account setup" :
            receiptAction === "resume" ? "Resume account setup" : "Set up payments"}</Button>}
      {receiptRead && (receiptUnavailable || receipt) && <Button size="small" variant="outlined" disabled={intent.isFetching}
        onClick={() => { void intent.refetch(); }}>Reload account setup status</Button>}
      {context.account_bound && <Button size="small" variant="outlined" disabled={working} onClick={() => void refresh()}>Refresh</Button>}
      {context.account_bound && <Button component={Link} to="/payments" size="small">Account settings</Button>}
    </Stack>
    {opened === scope && onboarding && <Suspense fallback={<CircularProgress aria-label="Loading Stripe" size={24} />}>
      <Embed actorScope={actorScope} context={context} purpose="onboarding" fetchSession={fetchSession}
        onExit={() => { setOpened(null); onChanged?.(); }} onRefresh={refresh} />
    </Suspense>}
  </Stack>;
}
